import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { filter, map, startWith } from 'rxjs';
import { AuthService } from '../core/auth/auth.service';
import { signInParams } from '../core/auth/return-url';
import { CartService } from '../core/cart/cart.service';
import { ClientMeService } from '../core/client/client-me.service';
import { initialsOf } from '../core/domain/person';
import { ProducerService } from '../core/producer/producer.service';

interface ClientDockTab {
  readonly path: string;
  readonly label: string;
  readonly icon: 'locate' | 'orders' | 'plan' | 'cart';
  readonly exact?: boolean;
}

/**
 * Dock bas mobile du parcours client — même logique pouce que l’espace
 * producteur. Visible uniquement sous 34 rem (CSS) ; le profil ouvre une
 * feuille de menus secondaires.
 */
const DOCK_TABS: readonly ClientDockTab[] = [
  { path: '/', label: 'Localiser', icon: 'locate', exact: true },
  { path: '/mes-commandes', label: 'Commandes', icon: 'orders' },
  { path: '/mes-retraits', label: 'Planning', icon: 'plan' },
  { path: '/panier', label: 'Panier', icon: 'cart' },
];

@Component({
  selector: 'ct-client-nav',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink, RouterLinkActive],
  template: `
    <nav class="dock" aria-label="Navigation client">
      @for (tab of tabs; track tab.path) {
        <a
          class="tab"
          [routerLink]="tab.path"
          routerLinkActive="active"
          [routerLinkActiveOptions]="tab.exact ? { exact: true } : { exact: false }"
          [attr.aria-label]="tabAria(tab)"
        >
          <span class="icon" aria-hidden="true">
            @switch (tab.icon) {
              @case ('locate') {
                <svg viewBox="0 0 24 24" width="22" height="22">
                  <path
                    fill="currentColor"
                    d="M12 2C8.1 2 5 5.1 5 9c0 5.2 7 13 7 13s7-7.8 7-13c0-3.9-3.1-7-7-7zm0 9.5c-1.4 0-2.5-1.1-2.5-2.5S10.6 6.5 12 6.5s2.5 1.1 2.5 2.5S13.4 11.5 12 11.5z"
                  />
                </svg>
              }
              @case ('orders') {
                <svg viewBox="0 0 24 24" width="22" height="22">
                  <path
                    fill="currentColor"
                    d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-9 14-5-5 1.4-1.4L10 14.2l7.6-7.6L19 8l-9 9z"
                  />
                </svg>
              }
              @case ('plan') {
                <svg viewBox="0 0 24 24" width="22" height="22">
                  <path
                    fill="currentColor"
                    d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V10h14v10zm0-12H5V6h14v2zM7 12h5v5H7v-5z"
                  />
                </svg>
              }
              @case ('cart') {
                <svg viewBox="0 0 24 24" width="22" height="22">
                  <path
                    fill="currentColor"
                    d="M7 18c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm10 0c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zM7.2 14h9.8c.8 0 1.4-.5 1.6-1.2L21 6H6.2l-.9-2H2v2h2l3.6 7.6L6.2 14c-.2.4-.3.8-.3 1.2 0 1.1.9 2 2 2h12v-2H7.4c-.1 0-.2-.1-.2-.2v-.1z"
                  />
                </svg>
              }
            }
          </span>
          @if (tab.icon === 'cart' && cartCount() > 0) {
            <span class="badge">{{ cartCount() > 9 ? '9+' : cartCount() }}</span>
          }
          <span class="caption">{{ tab.label }}</span>
        </a>
      }

      <button
        type="button"
        class="tab profile"
        [class.active]="profileOpen() || profileRouteActive()"
        [attr.aria-expanded]="profileOpen()"
        aria-controls="client-profile-sheet"
        [attr.aria-label]="profileLabel()"
        (click)="toggleProfile()"
      >
        @if (photoUrl(); as url) {
          <img class="avatar" [src]="url" alt="" aria-hidden="true" />
        } @else if (isAuthenticated()) {
          <span class="avatar initials" aria-hidden="true">{{ initials() }}</span>
        } @else {
          <span class="icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="22" height="22">
              <path
                fill="currentColor"
                d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z"
              />
            </svg>
          </span>
        }
        <span class="caption">Profil</span>
      </button>
    </nav>

    @if (profileOpen()) {
      <div class="sheet-backdrop" (click)="closeProfile()" aria-hidden="true"></div>
      <div
        id="client-profile-sheet"
        class="sheet"
        role="dialog"
        aria-modal="true"
        aria-label="Menu du compte"
      >
        <header class="sheet-head">
          <h2>Mon compte</h2>
          <button type="button" class="sheet-close" aria-label="Fermer" (click)="closeProfile()">
            ×
          </button>
        </header>

        @if (isAuthenticated()) {
          <p class="who">
            <span class="name">{{ displayName() }}</span>
            @if (profile()?.fullName) {
              <span class="email">{{ profile()?.email }}</span>
            }
          </p>
          <ul class="sheet-links">
            <li>
              <a routerLink="/profil" (click)="closeProfile()">Mon profil</a>
            </li>
            <li>
              <a routerLink="/preferences" (click)="closeProfile()">Préférences</a>
            </li>
            @if (hasProducerProfile()) {
              <li>
                <a routerLink="/producteur/tableau-de-bord" (click)="closeProfile()">
                  Espace producteur
                </a>
              </li>
            } @else {
              <li>
                <a routerLink="/vendre" (click)="closeProfile()">Vendre sur Champ’Table</a>
              </li>
            }
            @if (isAdmin()) {
              <li>
                <a routerLink="/admin" (click)="closeProfile()">Administration</a>
              </li>
            }
            <li>
              <button type="button" class="danger" (click)="signOut()">Se déconnecter</button>
            </li>
          </ul>
        } @else {
          <ul class="sheet-links">
            <li>
              <a
                class="primary"
                routerLink="/connexion"
                [queryParams]="signInQuery()"
                (click)="closeProfile()"
              >
                Se connecter
              </a>
            </li>
            <li>
              <a routerLink="/vendre" (click)="closeProfile()">Vendre sur Champ’Table</a>
            </li>
          </ul>
        }
      </div>
    }
  `,
  styles: `
    :host {
      display: contents;
    }

    .dock {
      display: none;
    }

    @media (width < 34rem) {
      .dock {
        position: fixed;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 40;
        display: flex;
        align-items: stretch;
        justify-content: space-around;
        gap: 0.125rem;
        padding: 0.35rem 0.2rem calc(0.35rem + env(safe-area-inset-bottom, 0px));
        background: var(--ct-white);
        border-top: 1px solid var(--ct-border);
        box-shadow: 0 -0.5rem 1.5rem rgb(61 46 34 / 8%);
      }
    }

    .tab {
      position: relative;
      flex: 1 1 0;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 0.15rem;
      min-width: 0;
      min-height: 3.25rem;
      padding: 0.25rem 0.1rem;
      border: 0;
      border-radius: var(--ct-radius-md);
      background: transparent;
      color: var(--ct-text-muted);
      text-decoration: none;
      font: inherit;
      cursor: pointer;

      &:hover {
        color: var(--ct-vert-cageot);
        background: var(--ct-kraft);
      }

      &:focus-visible {
        outline: 2px solid var(--ct-focus);
        outline-offset: 2px;
      }

      &.active {
        color: var(--ct-vert-cageot);
        font-weight: var(--ct-weight-bold);
      }
    }

    .icon {
      display: grid;
      place-items: center;
      width: 1.5rem;
      height: 1.5rem;
    }

    .caption {
      font-size: 0.6rem;
      line-height: 1.1;
      text-align: center;
    }

    .badge {
      position: absolute;
      top: 0.15rem;
      right: calc(50% - 1.1rem);
      min-width: 1rem;
      height: 1rem;
      padding: 0 0.2rem;
      border-radius: 999px;
      background: var(--ct-terre-cuite, #c17f59);
      color: var(--ct-kraft);
      font-size: 0.6rem;
      font-weight: var(--ct-weight-bold);
      line-height: 1rem;
      text-align: center;
    }

    .avatar {
      width: 1.65rem;
      height: 1.65rem;
      border-radius: 50%;
      object-fit: cover;
      display: block;
    }

    .avatar.initials {
      display: grid;
      place-items: center;
      background: var(--ct-vert-cageot);
      color: var(--ct-kraft);
      font-family: var(--ct-font-title);
      font-size: 0.7rem;
      font-weight: var(--ct-weight-bold);
    }

    .sheet-backdrop {
      position: fixed;
      inset: 0;
      z-index: 45;
      background: rgb(61 46 34 / 45%);
    }

    .sheet {
      position: fixed;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: 50;
      max-height: min(70dvh, 28rem);
      overflow: auto;
      padding: var(--ct-space-4) var(--ct-space-4)
        calc(var(--ct-space-4) + env(safe-area-inset-bottom, 0px));
      background: var(--ct-white);
      border-radius: var(--ct-radius-lg) var(--ct-radius-lg) 0 0;
      box-shadow: 0 -1rem 2.5rem rgb(0 0 0 / 18%);
    }

    .sheet-head {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: var(--ct-space-3);
      margin-bottom: var(--ct-space-3);

      h2 {
        margin: 0;
        font-size: var(--ct-size-lg);
        color: var(--ct-vert-cageot);
      }
    }

    .sheet-close {
      width: 2.25rem;
      height: 2.25rem;
      border: 1px solid var(--ct-border);
      border-radius: var(--ct-radius-sm);
      background: var(--ct-kraft);
      font-size: 1.35rem;
      line-height: 1;
      color: var(--ct-text-muted);
      cursor: pointer;
    }

    .who {
      margin: 0 0 var(--ct-space-3);
      display: flex;
      flex-direction: column;
      gap: 0.15rem;
    }

    .name {
      font-weight: var(--ct-weight-bold);
      color: var(--ct-text);
    }

    .email {
      font-size: var(--ct-size-sm);
      color: var(--ct-text-muted);
    }

    .sheet-links {
      list-style: none;
      margin: 0;
      padding: 0;
      display: flex;
      flex-direction: column;
      gap: 0.125rem;

      a,
      button {
        display: block;
        width: 100%;
        padding: var(--ct-space-2) var(--ct-space-3);
        border: 0;
        border-radius: var(--ct-radius-sm);
        background: transparent;
        color: var(--ct-text);
        text-align: left;
        text-decoration: none;
        font: inherit;
        cursor: pointer;

        &:hover {
          background: var(--ct-kraft);
        }
      }

      a.primary {
        background: var(--ct-vert-cageot);
        color: var(--ct-kraft);
        font-weight: var(--ct-weight-bold);
        text-align: center;

        &:hover {
          filter: brightness(1.05);
          background: var(--ct-vert-cageot);
        }
      }

      .danger {
        color: var(--ct-terre-cuite, #c17f59);
      }
    }

    @media print {
      .dock,
      .sheet,
      .sheet-backdrop {
        display: none !important;
      }
    }
  `,
})
export class CtClientNav {
  private readonly router = inject(Router);
  private readonly auth = inject(AuthService);
  private readonly clientMe = inject(ClientMeService);
  private readonly producer = inject(ProducerService);
  private readonly cart = inject(CartService);

  readonly signedOut = output<void>();

  protected readonly tabs = DOCK_TABS;
  protected readonly profileOpen = signal(false);
  protected readonly isAuthenticated = this.auth.isAuthenticated;
  protected readonly profile = this.auth.profile;

  private readonly url = toSignal(
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => this.router.url),
      startWith(this.router.url),
    ),
    { requireSync: true },
  );

  protected readonly photoUrl = computed(() => this.clientMe.photoUrl());
  protected readonly cartCount = computed(() => this.cart.itemCount());
  protected readonly hasProducerProfile = computed(() => this.producer.hasProfile() === true);
  protected readonly isAdmin = computed(() => this.profile()?.groups?.includes('admins') ?? false);

  protected readonly initials = computed(() => {
    const profile = this.profile();
    return initialsOf(profile?.fullName ?? profile?.email ?? '?');
  });

  protected readonly displayName = computed(() => {
    const profile = this.profile();
    return profile?.fullName?.trim() || profile?.email || 'Compte';
  });

  protected readonly profileLabel = computed(() => {
    if (!this.isAuthenticated()) {
      return 'Compte';
    }
    return `Profil — ${this.displayName()}`;
  });

  protected readonly profileRouteActive = computed(() => {
    const path = (this.url().split('?')[0] ?? '').split('#')[0] ?? '';
    return path === '/profil' || path === '/preferences' || path === '/admin';
  });

  constructor() {
    void this.producer.ensureLoaded();
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe(() => this.profileOpen.set(false));
  }

  protected tabAria(tab: ClientDockTab): string {
    if (tab.icon === 'cart' && this.cartCount() > 0) {
      return `${tab.label} (${this.cartCount()})`;
    }
    return tab.label;
  }

  protected signInQuery(): Record<string, string> {
    return signInParams(this.router.url);
  }

  protected toggleProfile(): void {
    this.profileOpen.update((open) => !open);
  }

  protected closeProfile(): void {
    this.profileOpen.set(false);
  }

  protected async signOut(): Promise<void> {
    this.closeProfile();
    this.signedOut.emit();
  }
}
